/* @pareto-engineering/generator-front 1.0.12 */
import React, {
  useMemo,
  useState,
} from 'react'

import styleNames from '@pareto-engineering/bem/exports'

import {
  AnimatedGradient,
  Page,
  useApp,
  Button,
} from '@pareto-engineering/design-system'

import {
  Customers,
  SectionHero,
  SectionCTA,
  Steps,
  ExpertCard,
  TestimonialCard,
  Footer,
  NavigationBar,
  ConversionPage,
  UseCaseCard,
  InteractiveMap,
  aiSectionMap,
} from 'modules/components'

import {
  aiLogos,
  aiTestimonials,
  locations,
} from 'modules/common/content'

import {
  SITE,
} from 'modules/urls'

import { useCases } from './content/Home'

const baseClassName = styleNames.base

const componentClassName = 'page-home'

const experts = [
  {
    name      :'Anna',
    identifier:'25-30/F',
    location  :'Manhattan, USA',
    image     :'/profiles/Anna.jpg',
    role      :'Database and SAP Technician',
    languages :['English', 'Portuguese'],
    tags      :['Scripting', 'Reinforcement Learning', 'Semantic Segmentation', 'Sentiment Analysis Specialist'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'55,000',
      },
    ],
  },
  {
    name      :'Tim',
    identifier:'30-40/M',
    role      :'Financial Compliance Manager',
    image     :'/profiles/Tim.jpg',
    location  :'Kansas City, USA',
    languages :['English', 'German'],
    tags      :['Prompt Engineering', 'RLHF', 'Technical Writing Trainer', 'Safety', 'Alignment', 'Adversarial Content Master'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'83,000',
      },
    ],
  },
  {
    name      :'Zethara',
    identifier:'50+/F',
    role      :'Published Author, Poet',
    image     :'/profiles/Zethara.jpg',
    location  :'El Paso, USA',
    languages :['English'],
    tags      :['Creative Content Training', 'Long-tail Uniqueness', 'Custom Hallucination Inducement', 'Multimedia Trainer'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'832,000',
      },
    ],
  },
  {
    name      :'Wells',
    identifier:'30-40/M',
    role      :'Aviation Electrician, Quality Assurance Team',
    image     :'/profiles/Wells.jpg',
    location  :'Miami, USA',
    languages :['English'],
    tags      :['Bias Training', 'Safety', 'Alignment', 'Adversarial Content Specialist'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'110,000',
      },
    ],
  },
  {
    name      :'Lexie',
    identifier:'25-35/F',
    role      :'Stay at home mom',
    image     :'/profiles/Lexie.jpg',
    location  :'Rochester, USA',
    languages :['English'],
    tags      :['Fluency Training', 'Prompt Engineering', 'Image Classification', 'Sheet Scripting and Dialogs'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'130,000',
      },
    ],
  },
  {
    name      :'Distance',
    identifier:'30-40/F',
    role      :'B.A., Elementary Education',
    image     :'/profiles/Distance.jpg',
    location  :'Chicago, USA',
    languages :['English'],
    tags      :['RLHF', 'ML Process Annotator', 'Model Precision', 'Semantic Segmentation'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.8%',
      },
      {
        label:'Tasks completed',
        value:'610,000',
      },
    ],
  },
  {
    name      :'Donovan',
    identifier:'40-50/M',
    role      :'B.S., Accounting',
    image     :'/profiles/Donovan.jpg',
    location  :'Philadelphia, USA',
    languages :['English'],
    tags      :['Reinforcement Learning', 'Document Indexing', 'Span Categorization', 'Item Similarity', 'Archive Classification'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.8%',
      },
      {
        label:'Tasks completed',
        value:'500,000',
      },
    ],
  },
  {
    name      :'Soraya',
    identifier:'25-30/F',
    role      :'B.A., Psychology',
    image     :'/profiles/Soraya.jpg',
    location  :'Oakland, USA',
    languages :['English'],
    tags      :['Content Moderation', 'Safety', 'Alignment', 'Adversarial Content Specialist', 'RLHF', 'Prompt Engineering'],
    stats     :[
      {
        label:'Approval rate',
        value:'99.9%',
      },
      {
        label:'Tasks completed',
        value:'163,000',
      },
    ],
  },
]

const steps = [
  {
    title      :'Describe your project',
    description:'We help you develop clear project guidelines, determine the ideal labeling team, and set a cost-effective hourly rate to fit your timeline.',
  },
  {
    title      :'Match with top labelers',
    description:'We assemble your team same-day from our vetted network. If you have unique needs, we can find the right experts in just 3-5 days.',
  },
  {
    title      :'Fully managed & quality assured',
    description:'We support labelers to deliver the highest quality data with paid trials, expert-review & feedback, gold standard items, and more QA techniques.',
  },
]

const Home = () => {
  const app = useApp()

  const [useCaseCount, setUseCaseCount] = useState(6)

  const helmetProps = useMemo(() => ({
    title           :'Pareto: Premium AI & LLM Training Data Labeled by Elite Teams',
    meta_description:'Pareto matches AI companies with expert-vetted data labelers to fine-tune cutting-edge AI/LLM models. Meet the top 0.01% of AI labelers today.',
    canonical       :SITE.HOME,
    og_type         :'website',
    main_image      :`${app.SITE.CANONICAL}/misc/social-share.jpg`,
  }), [])

  const customersMap = aiLogos.map((logo) => ({ target: logo.target, altText: logo.target }))

  return (
    <ConversionPage
      conversionFormConfig={aiSectionMap}
      mobileButtonText="Get started"
      desktopLaunchText="Join leading AI companies in harnessing world-class AI talent."
      mobileLaunchText="Harness world-class AI talent."
      id="ai-home"
      helmetProps={helmetProps}
      Background={AnimatedGradient}
      className={[
        baseClassName,
        componentClassName,
      ]
        .filter((e) => e)
        .join(' ')}
    >
      <NavigationBar />

      <SectionHero
        className="to-plain"
        heading={(
          <>
            <span>
              Premium AI & LLM training data labeled by
            </span>
            {' '}
            <span className="x-highlighted c-x">
              elite teams
            </span>
          </>
        )}
        subtitle="Our talent-first data collection platform matches AI companies with expert-vetted data labelers. From same-day experimental data to fully managed teams, fine-tune any dimension of your AI/LLM model with the highest quality data at unbeatable prices."
        conversionButtonText="Get started"
        secondaryButtonText="Explore profiles"
        secondaryButtonPath="#profiles"
      />

      <Page.Section
        id="customers"
        className="x-background-far b-x"
      >
        <Customers
          className="span-8 md-start-2 md-span-12 x-main"
          items={customersMap}
          wrap
        />
      </Page.Section>

      <span
        id="profiles"
        className="anchor"
      />

      <Page.Section
        id="labelers"
        className="x-background-far b-x"
        style={{
          paddingTop:'calc(var(--theme-default-section-spacing) / 2)',
        }}
      >
        <p className="span-8 md-start-2 md-span-3 x-paragraph c-x s1">
          Work with diverse & vetted professionals
        </p>
        <div className="span-8 md-start-6 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              Introducing the top 0.01% of AI data labelers
            </span>
          </p>
          <p className="span-grid s2">
            We are a globally distributed network of
            {' '}
            <span className="x-highlighted c-x">
              master annotators, evaluators, and prompt engineers
            </span>
            {' '}
            with proven track record successfully completing 2.5M+
            tasks for the world&apos;s largest AI/ML organizations.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        className="x-background-far b-x"
      >
        <ExpertCard.Group
          className="x-background-far b-x span-8 md-span-14"
          type="snap-scroller"
          desktopType="snap-scroller"
        >
          <div className="hide-on-mobile md-span-1 manual" />
          {
            experts.map((expert, i) => (
              <ExpertCard
                className="s-1 span-6 md-span-4 manual"
                id={`expert-${i + 1}`}
                {...expert}
              />
            ))
          }
        </ExpertCard.Group>
        <p className="md-start-6 md-span-6 span-8 x-paragraph c-x s1">
          Fictitious images and names are used to protect privacy.
          All degrees, skills, and accreditations are verified.
        </p>
      </Page.Section>

      <Page.Section
        className="x-background-far b-x"
        isSpaced
      >
        <p className="h1 md-start-2 md-span-5 span-8 remove-margin">
          <span className="x-highlighted c-x">Harness the potential of world-class AI talent</span>
        </p>
        <img
          alt="Harness Pareto AI"
          src="/misc/potential.webp"
          className="md-start-8 md-span-5 span-8"
          style={{
            width       :'100%',
            borderRadius:'var(--theme-default-border-radius)',
            objectFit   :'cover',
            gridRow     :'span 3',
          }}
        />
        <p className="md-start-2 md-span-5 span-8 s2">
          Partner with experienced labelers for unique insights to refine your labels,
          improve guidelines, and track team performance. Ensure
          {' '}
          <span className="x-highlighted c-x">
            smooth setup and cost-effective execution
          </span>
          {' '}
          whatever the use case.
        </p>
      </Page.Section>

      <Page.Section
        id="use-cases"
        className="x-background-far b-x"
      >
        <div className="span-8 md-start-2 md-span-12 grid">
          {
            useCases.slice(0, useCaseCount).map((useCase) => (
              <UseCaseCard
                {...useCase}
                className="span-8 md-span-4 revealed"
              />
            ))
          }
          {
            useCases.slice(useCaseCount).map((useCase) => (
              <UseCaseCard
                {...useCase}
                className="span-8 md-span-4 hidden"
              />
            ))
          }
        </div>
        {
          useCaseCount < useCases.length && (
            <div
              className="md-start-7 md-span-2 span-8"
              style={{
                display       :'flex',
                justifyContent:'center',
              }}
            >
              <Button
                isGhost
                color="interactive"
                onClick={() => setUseCaseCount(useCaseCount + 3)}
              >
                Load more
              </Button>
            </div>
          )
        }
      </Page.Section>

      <Page.Section
        id="unbeatable-rates"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-3 x-paragraph c-x s1">
          Transparent & unbeatable rates
        </p>
        <div className="span-8 md-start-6 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              On demand and cost-effective pricing
            </span>
          </p>
          <p className="span-grid s2">
            Start with no minimums &
            {' '}
            <span className="x-highlighted c-x">
              pay as you go
            </span>
            {' '}
            for quality-assured results. Fair hourly rates are set based on the expertise and
            skills of the workforce you need.
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="steps"
        className="x-background-far b-x"
      >
        <p className="span-8 md-start-2 md-span-3 h1 remove-top-margin">
          <span className="x-highlighted c-x">
            How it works
          </span>
        </p>
        <Steps
          items={steps}
          className="span-8 md-start-6 md-span-6"
        />
      </Page.Section>

      <Page.Section
        id="crowdwork"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-3 x-paragraph c-x s1">
          Redefining ethical crowd work
        </p>
        <div className="span-8 md-start-6 md-span-6 grid">
          <p className="h1 span-grid remove-margin">
            <span className="x-highlighted c-x">
              Built by and for a new generation of labelers
            </span>
          </p>
          <p className="span-grid s2">
            The infrastructure behind human data collection is outdated.
            We&apos;ve joined forces with seasoned data labelers and crowd work researchers
            to redefine the relationship between workers and requesters.
          </p>
          <p className="span-grid remove-top-margin s2">
            Pareto operates on the principles of equitable compensation, collaborative management,
            and expert evaluation & feedback. Our mission is to
            {' '}
            <span className="x-highlighted c-x">
              empower talented & diverse professionals worldwide to contribute to AI training.
            </span>
          </p>
        </div>
      </Page.Section>

      <Page.Section
        id="map"
        className="x-background-far b-x"
      >
        <InteractiveMap
          color="metadata"
          className="span-grid"
          locations={locations}
        />
      </Page.Section>

      <Page.Section
        id="testimonials"
        className="x-background-far b-x"
      >
        <p className="h1 md-start-2 md-span-8 span-8">
          <span className="x-highlighted c-x">
            Impressions from our community
          </span>
        </p>
        <TestimonialCard.Group
          className="span-8 md-start-2 md-span-12"
          desktopType="grid"
          type="snap-scroller"
        >
          {
            aiTestimonials.map((testimonial, i) => (
              <TestimonialCard
                className="span-7 md-span-6 manual-mobile-only"
                id={`testimonial-${i + 1}`}
                {...testimonial}
              />
            ))
          }
        </TestimonialCard.Group>
      </Page.Section>

      <SectionCTA
        className="from-plain"
        heading={
          (
            <span className="x-highlighted c-x">
              Fine-tune your AI model with the highest quality human data
            </span>
          )
        }
        subtitle="Refine every facet of your AI with elite teams of expert-vetted data labelers at unbeatable prices."
        buttonText="Get started"
      />
      <Footer />
    </ConversionPage>
  )
}

export default Home
